import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import footprintStamp from "@/assets/footprint-stamp.png";

interface MidCTAProps {
  headline?: string;
  subtext?: string;
  variant?: "light" | "dark";
  primaryButtonLabel?: string;
  primaryButtonLink?: string;
  showSecondaryButton?: boolean;
}

export function MidCTA({
  headline = "This Is Your Next Step",
  subtext = "Join thousands of believers who are restoring discernment and rebuilding faith.",
  variant = "light",
  primaryButtonLabel = "Start Your Healing Journey",
  primaryButtonLink,
  showSecondaryButton = true,
}: MidCTAProps) {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const isDark = variant === "dark";

  return (
    <section className={`py-16 md:py-20 overflow-hidden ${isDark ? "bg-foreground" : "bg-card"}`}>
      <div className="container px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Footprint Stamp — identical to Hero */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring", damping: 12 }}
            className="flex items-center justify-center gap-6 mb-10"
          >
            <div className="relative">
              <div className="absolute -inset-2 rounded-full border-[3px] border-red-500/80 animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
              <motion.img
                src={footprintStamp}
                alt="Stamping out spiritual abuse"
                className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 relative z-10 drop-shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                animate={{ rotate: [0, -8, 0, 8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
            <div className="text-left">
              <p
                className="text-base md:text-lg font-black text-red-400 uppercase tracking-[0.3em]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Stamp Out
              </p>
              <p
                className={`text-2xl md:text-3xl lg:text-4xl font-black uppercase tracking-[0.1em] ${isDark ? "text-primary-foreground" : "text-foreground"}`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Spiritual Abuse
              </p>
            </div>
          </motion.div>

          <motion.h3
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`font-serif text-4xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.1] mb-5 ${
              isDark ? "text-primary-foreground" : "text-foreground"
            }`}
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {headline.split(" ").map((word, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                className="inline-block mr-[0.3em]"
              >
                {word}
              </motion.span>
            ))}
          </motion.h3>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className={`text-lg md:text-xl mb-8 max-w-2xl mx-auto ${
              isDark ? "text-primary-foreground/60" : "text-muted-foreground"
            }`}
          >
            {subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              onClick={() => {
                if (loading) return;
                if (primaryButtonLink) {
                  navigate(primaryButtonLink);
                } else {
                  navigate(user ? "/dashboard" : "/auth");
                }
              }}
              className={`h-14 px-8 text-base font-bold rounded-full ${
                isDark
                  ? "bg-white text-foreground hover:bg-white/90"
                  : ""
              }`}
            >
              {primaryButtonLabel}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            {showSecondaryButton && (
              <Button
                variant="outline"
                onClick={() => navigate("/certification")}
                className={`h-14 px-8 text-base font-bold rounded-full ${
                  isDark
                     ? "border-2 border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-all duration-300"
                     : ""
                }`}
              >
                Get Certified
              </Button>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
